import { useState } from 'react';
import { Tag, Typography, theme } from 'antd';
import { useTranslation } from 'react-i18next';
import type { Lead } from '@entities/crm';
import { UserCell } from '@entities/user-directory';

const { Text } = Typography;

interface LeadCardProps {
  lead: Lead;
  /** Column hue — used for the card's accent rail and hover ring. */
  accent: string;
  dragging: boolean;
  onDragStart: () => void;
  onDragEnd: () => void;
  onClick: () => void;
}

const SOURCE_COLOR = {
  RFQ_TARGET: 'blue',
  QUOTE: 'purple',
  ORDER: 'success',
  MANUAL: 'default',
} as const;

export function LeadCard({
  lead,
  accent,
  dragging,
  onDragStart,
  onDragEnd,
  onClick,
}: LeadCardProps) {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const [hovered, setHovered] = useState(false);

  return (
    <article
      draggable
      onDragStart={(e) => {
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', lead.id);
        onDragStart();
      }}
      onDragEnd={onDragEnd}
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        cursor: 'grab',
        position: 'relative',
        overflow: 'hidden',
        background: token.colorBgContainer,
        border: `1px solid ${hovered && !dragging ? accent : token.colorBorderSecondary}`,
        borderRadius: 12,
        padding: '12px 12px 12px 15px',
        boxShadow: dragging
          ? 'none'
          : hovered
            ? '0 4px 14px -4px rgba(0,0,0,0.14)'
            : '0 1px 2px rgba(0,0,0,0.06)',
        transform: hovered && !dragging ? 'translateY(-1px)' : 'none',
        opacity: dragging ? 0.5 : 1,
        transition: 'box-shadow 180ms ease, transform 180ms ease, border-color 180ms ease',
        display: 'flex',
        flexDirection: 'column',
        gap: 9,
      }}
    >
      {/* Accent rail ties the card to its column even while being dragged. */}
      <span
        aria-hidden
        style={{
          position: 'absolute',
          left: 0,
          top: 0,
          bottom: 0,
          width: 3,
          background: accent,
        }}
      />

      {/* Columns get narrow at five-across, and the source label can be long
          ("Адресная заявка"). Wrapping drops the tag onto its own line instead
          of letting it run under the card's overflow clip. */}
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 6, flexWrap: 'wrap' }}>
        <span style={{ fontWeight: 600, fontSize: 13, lineHeight: 1.35, flex: '1 1 auto', minWidth: 0 }}>
          {lead.title}
        </span>
        <Tag
          color={SOURCE_COLOR[lead.source]}
          style={{
            marginInlineEnd: 0,
            flex: '0 0 auto',
            maxWidth: '100%',
            fontSize: 10,
            borderRadius: 6,
            height: 20,
            lineHeight: '18px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {t(`crm.source.${lead.source}`)}
        </Tag>
      </div>

      {lead.buyerId ? <UserCell userId={lead.buyerId} size="xs" /> : null}

      {lead.notes ? (
        <Text
          type="secondary"
          style={{
            fontSize: 12,
            lineHeight: 1.45,
            paddingLeft: 8,
            borderLeft: `2px solid ${token.colorBorderSecondary}`,
          }}
          ellipsis
        >
          {lead.notes}
        </Text>
      ) : null}

      <Text
        type="secondary"
        style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.4 }}
      >
        {t('crm.card.updated', { date: new Date(lead.updatedAt).toLocaleDateString() })}
      </Text>
    </article>
  );
}
